
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { Loader2, AlertCircle, CheckCircle, Send, RefreshCw, Info, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

export function EmailTestTool() {
  const [testEmail, setTestEmail] = useState("");
  const [testResult, setTestResult] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  const [isSmtpWarningVisible, setIsSmtpWarningVisible] = useState(true);
  const { toast } = useToast();

  const addLog = (message: string) => {
    setLogs(prevLogs => [...prevLogs, `[${new Date().toLocaleTimeString()}] ${message}`]);
  };

  const clearLogs = () => {
    setLogs([]);
    setTestResult(null);
  };

  const openSupabaseEmailSettings = () => {
    window.open("https://supabase.com/dashboard/project/axkwrnkqyuegftszrwne/auth/templates", "_blank");
  };

  const openEdgeFunctionLogs = () => {
    window.open("https://supabase.com/dashboard/project/axkwrnkqyuegftszrwne/functions/test-resend/logs", "_blank");
  };

  const testResendConfiguration = async () => {
    if (!testEmail) {
      toast({
        title: "Email adres vereist",
        description: "Vul een geldig email adres in om de test uit te voeren",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    addLog(`Test gestart voor email: ${testEmail}`);

    try {
      // Test de Resend API configuratie
      addLog("Testen van Resend API configuratie...");
      const { data: resendTest, error: resendError } = await supabase.functions.invoke("test-resend", {
        body: { email: testEmail }
      });

      if (resendError) {
        addLog(`❌ Resend API test gefaald: ${resendError.message}`);
        throw resendError;
      }

      addLog(`✅ Resend API configuratie test resultaat: ${JSON.stringify(resendTest)}`);
      
      // Test het versturen van een test email
      addLog("Versturen van test email...");
      const { data: emailTest, error: emailError } = await supabase.functions.invoke("send-confirmation-email", {
        body: { email: testEmail, type: "test" }
      });

      if (emailError) {
        addLog(`❌ Test email versturen gefaald: ${emailError.message}`);
        throw emailError;
      }

      addLog(`✅ Test email verzonden: ${JSON.stringify(emailTest)}`);
      
      setTestResult({
        success: true,
        resendTest,
        emailTest,
      });
      
      toast({
        title: "Test succesvol",
        description: "Alle email tests zijn succesvol uitgevoerd. Controleer je inbox.",
      });
    } catch (error) {
      console.error("Email test error:", error);
      addLog(`❌ Fout bij uitvoeren tests: ${error.message}`);
      
      setTestResult({
        success: false,
        error: error.message,
      });
      
      toast({
        title: "Test mislukt",
        description: `Er is een fout opgetreden: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Email configuratie tester</CardTitle>
        <CardDescription>
          Test de email configuratie door een test email te versturen
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isSmtpWarningVisible && (
          <Alert className="mb-4 bg-amber-50">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Let op Supabase SMTP instellingen</AlertTitle>
            <AlertDescription>
              <p className="mb-2">
                Zorg ervoor dat "Enable Custom SMTP" is uitgeschakeld in je Supabase Email Settings als je Resend wilt gebruiken.
              </p>
              <div className="flex justify-between items-center mt-3">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={openSupabaseEmailSettings}
                  className="flex items-center">
                  <ExternalLink className="mr-1 h-3 w-3" />
                  Supabase Email Settings
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setIsSmtpWarningVisible(false)}
                >
                  Sluiten
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        <div className="flex space-x-2">
          <Input
            placeholder="email@voorbeeld.nl"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            className="flex-1"
          />
          <Button 
            onClick={testResendConfiguration} 
            disabled={isLoading || !testEmail}
            className="whitespace-nowrap"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Testen...
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                Test configuratie
              </>
            )}
          </Button>
        </div>

        {testResult && (
          <Alert variant={testResult.success ? "default" : "destructive"}>
            {testResult.success ? (
              <CheckCircle className="h-4 w-4" />
            ) : (
              <AlertCircle className="h-4 w-4" />
            )}
            <AlertTitle>
              {testResult.success 
                ? "Email configuratie is correct" 
                : "Email configuratie probleem"}
            </AlertTitle>
            <AlertDescription>
              {testResult.success 
                ? "De email configuratie werkt correct. Er is een test email verzonden." 
                : `Fout bij testen van email configuratie: ${testResult.error}`}
            </AlertDescription>
          </Alert>
        )}

        <Separator className="my-4" />

        <div className="flex flex-col space-y-2">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <Info className="h-4 w-4 text-blue-500" />
              <h3 className="text-sm font-medium">Resend SMTP Configuratie Checklist</h3>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={openEdgeFunctionLogs}
              className="flex items-center text-xs"
            >
              <ExternalLink className="mr-1 h-3 w-3" />
              Edge Function Logs
            </Button>
          </div>
          
          <div className="text-sm p-3 bg-gray-50 dark:bg-gray-900 rounded-md space-y-2">
            <div className="flex items-center">
              <span className="font-medium w-40">1. Resend API Key:</span>
              <span>Ingesteld als secret in Supabase</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-40">2. Domein verificatie:</span>
              <span>Controleer of je domein is geverifieerd in Resend</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-40">3. Edge Functions:</span>
              <span>test-resend en send-confirmation-email</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-40">4. Supabase SMTP:</span>
              <span>Zorg dat Custom SMTP is uitgeschakeld</span>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-sm font-medium">Test logs</h3>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={clearLogs}
              className="h-8 px-2 text-xs"
            >
              <RefreshCw className="mr-1 h-3 w-3" />
              Wis logs
            </Button>
          </div>
          <Textarea
            className="font-mono text-xs h-40"
            readOnly
            value={logs.join('\n')}
          />
        </div>
      </CardContent>
      <CardFooter className="text-xs text-muted-foreground flex justify-between">
        <span>Controleer de Edge Function logs in Supabase voor meer details</span>
        <Button
          variant="link"
          size="sm"
          className="p-0 h-auto"
          onClick={openEdgeFunctionLogs}
        >
          Bekijk logs
        </Button>
      </CardFooter>
    </Card>
  );
}
